import { BrowseNavigationItemSelected, BrowseNavigationItemStyle, BrowseNavigationItemType } from '@udemy/browse-event-tracking';
import { Tracker } from '@udemy/event-tracking';
import { useGetExamVouchersQuery } from '../graphql/api-platform-graphql';
import { BrowseNavItemType } from '../types/navigation';

/**
 * Represents a single exam voucher item after transformation
 */

/**
 * Transform GraphQL exam vouchers response into flat ExamVoucherItem array.
 * Filters out null items and items not available for the user's region.
 */
export function transformExamVouchersResponse(data) {
  const items = data.certificationVoucherExams.items;
  if (!items) {
    return [];
  }
  return items.filter(item => item != null).filter(item => item.isAvailableForRegion).map(item => ({
    id: item.id,
    slug: item.slug,
    title: item.title,
    isAvailableForRegion: item.isAvailableForRegion,
    providerName: item.providerDetails.name,
    providerType: item.providerDetails.type
  }));
}

/**
 * Group exam vouchers by provider type into an L2/L3 navigation structure.
 *
 * L2: One BrowseNavItem per provider type (e.g., "AWS Certification Exam Vouchers")
 * L3: Individual voucher items as children (e.g., "AWS Certified AI Practitioner")
 *
 * Grouping is dynamic — new provider types from the backend appear automatically.
 */
export function groupExamVouchersByProvider(vouchers) {
  const grouped = new Map();
  const style = BrowseNavigationItemStyle.NAVIGATION_DROPDOWN;
  for (const voucher of vouchers) {
    const existing = grouped.get(voucher.providerType);
    if (existing) {
      existing.push(voucher);
    } else {
      grouped.set(voucher.providerType, [voucher]);
    }
  }
  return Array.from(grouped.entries()).map(([providerType, items]) => {
    var _items$0$providerName, _items$;
    const providerName = (_items$0$providerName = (_items$ = items[0]) === null || _items$ === void 0 ? void 0 : _items$.providerName) !== null && _items$0$providerName !== void 0 ? _items$0$providerName : providerType;
    return {
      id: `exam-voucher-provider-${providerType}`,
      type: BrowseNavItemType.ITEM,
      title: `${providerName} Certification Exam Vouchers`,
      children: items.map(voucher => ({
        id: `exam-voucher-${voucher.id}`,
        type: BrowseNavItemType.ITEM,
        title: voucher.title,
        url: `/certification-vouchers/${voucher.slug}/`,
        onClick: () => {
          Tracker.publishEvent(new BrowseNavigationItemSelected({
            id: voucher.id,
            type: BrowseNavigationItemType.CERTIFICATION_VOUCHER,
            uiRegion: 'main_nav',
            style
          }));
        }
      }))
    };
  });
}

/** Page size for exam vouchers list (cursor-based pagination). Request up to 100 items per request. */
const EXAM_VOUCHERS_PAGE_LIMIT = 100;

/**
 * Hook to fetch and transform exam vouchers data from GraphQL.
 * Returns both the flat voucher list and the grouped L2/L3 nav structure.
 * Requests up to EXAM_VOUCHERS_PAGE_LIMIT (100) items in a single page.
 */
export function useFetchExamVouchers() {
  const queryResult = useGetExamVouchersQuery({
    pageInfo: {
      limit: EXAM_VOUCHERS_PAGE_LIMIT
    }
  });
  const vouchers = queryResult.data ? transformExamVouchersResponse(queryResult.data) : undefined;
  return {
    ...queryResult,
    vouchers,
    browseNavItems: vouchers ? groupExamVouchersByProvider(vouchers) : undefined
  };
}

/**
 * Standalone fetcher function for exam vouchers data.
 * Useful inside fetchChildren callbacks where hooks cannot be used.
 * Requests up to EXAM_VOUCHERS_PAGE_LIMIT (100) items in a single page.
 */
export async function fetchExamVouchers() {
  const fetcherFn = useGetExamVouchersQuery.fetcher({
    pageInfo: {
      limit: EXAM_VOUCHERS_PAGE_LIMIT
    }
  });
  const data = await fetcherFn();
  return transformExamVouchersResponse(data);
}

/**
 * Standalone fetcher that returns exam vouchers grouped by provider as BrowseNavItem array.
 * Ready to be used directly as fetchChildren return value.
 */
export async function fetchExamVouchersAsBrowseNavItems() {
  const vouchers = await fetchExamVouchers();
  return groupExamVouchersByProvider(vouchers);
}

/**
 * URL for the "All Certification Exam Vouchers" landing page.
 * Shared between desktop Get Certified nav and mobile aggregator nav.
 */
export const ALL_CERTIFICATION_EXAM_VOUCHERS_URL = '/all-certification-vouchers/';
//# sourceMappingURL=fetch-exam-vouchers.js.map